import React from 'react'
import moment from 'moment'
import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined'
import useIsMobile from '../../../helpers/useIsMobile'
import { getTeamColorKey, resolveColor } from './taskHelpers'

const Dot = ({ color, size = 8, sx }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: color,
      flexShrink: 0,
      ...sx,
    }}
  />
)

const SectionLabel = ({ children, sx }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      px: 1.25,
      ...sx,
    }}
  >
    {children}
  </Typography>
)

const FilterRow = ({ icon, label, count, selected, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1.25}
    onClick={onClick}
    sx={{
      height: 38,
      px: 1.25,
      borderRadius: 1,
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: selected ? 600 : 400,
      color: 'text.primary',
      bgcolor: selected ? 'anMuted.main' : 'transparent',
      '&:hover': { bgcolor: 'anMuted.main' },
    }}
  >
    {icon}
    <Typography
      component="span"
      noWrap
      sx={{ flex: 1, minWidth: 0, fontSize: 'inherit', fontWeight: 'inherit' }}
    >
      {label}
    </Typography>
    <Typography variant="caption" color="text.secondary" fontWeight={500}>
      {count}
    </Typography>
  </Stack>
)

// Left-hand rail: what needs doing today, and which slice of the board to
// show. Collapses to a strip + chip row below `lg`, where it sits above the
// board rather than beside it.
const TaskFocusRail = ({
  focus,
  counts,
  scope,
  onScopeChange,
  includeCompleted,
  onIncludeCompletedChange,
}) => {
  const theme = useTheme()
  const compact = useIsMobile('lg')
  const needAction = focus.overdue + focus.today

  const scopeChips = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'me', label: 'Mine', count: counts.me },
    ...counts.teams.map((team) => ({
      key: `team:${team.name}`,
      label: team.name,
      count: team.count,
      color: resolveColor(theme, getTeamColorKey(team.name)),
    })),
  ]

  if (compact) {
    return (
      <Stack gap={1.5}>
        <Paper
          variant="outlined"
          sx={{ px: 1.75, py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <Typography sx={{ fontSize: '2rem', fontWeight: 600, lineHeight: 1 }}>
            {needAction}
          </Typography>
          <Stack gap={0.25}>
            <Typography variant="smallGrey">need action today</Typography>
            <Stack direction="row" gap={1.5}>
              <Typography
                variant="caption"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <Dot color="anRed.main" />
                {focus.overdue} overdue
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <Dot color="anOrange.main" />
                {focus.today} due today
              </Typography>
            </Stack>
          </Stack>
          <FormControlLabel
            sx={{ ml: 'auto', mr: 0 }}
            control={
              <Switch
                size="small"
                checked={includeCompleted}
                onChange={(e) => onIncludeCompletedChange(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Completed
              </Typography>
            }
          />
        </Paper>
        <Stack direction="row" gap={0.75} sx={{ overflowX: 'auto', pb: 0.5 }}>
          {scopeChips.map((chip) => (
            <Chip
              key={chip.key}
              label={`${chip.label} · ${chip.count}`}
              onClick={() => onScopeChange(chip.key)}
              icon={chip.color ? <Dot color={chip.color} /> : undefined}
              sx={{
                borderRadius: 1,
                flexShrink: 0,
                pl: chip.color ? 0.75 : 0,
                ...(scope === chip.key
                  ? { bgcolor: 'primary.main', color: 'primary.contrastText' }
                  : { bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }),
              }}
            />
          ))}
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack gap={2} sx={{ width: 292, flexShrink: 0 }}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack gap={1.5}>
          <SectionLabel sx={{ px: 0 }}>{moment().format('dddd D MMMM')}</SectionLabel>
          <Stack direction="row" alignItems="baseline" gap={1}>
            <Typography sx={{ fontSize: '2.4rem', fontWeight: 600, lineHeight: 1 }}>
              {needAction}
            </Typography>
            <Typography variant="body2">need action today</Typography>
          </Stack>
          <Stack gap={0.75}>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.8rem' }}
            >
              <Dot color="anRed.main" />
              {focus.overdue} overdue
              {focus.oldestOverdueDays > 0 && (
                <Box component="span" sx={{ ml: 'auto', color: 'text.disabled', fontSize: '0.75rem' }}>
                  oldest {focus.oldestOverdueDays} day{focus.oldestOverdueDays === 1 ? '' : 's'}
                </Box>
              )}
            </Typography>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.8rem' }}
            >
              <Dot color="anOrange.main" />
              {focus.today} due today
            </Typography>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.8rem' }}
            >
              <Dot color="text.disabled" />
              {focus.week} due later this week
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1 }}>
        <Stack gap={0.25}>
          <SectionLabel sx={{ pt: 0.75, pb: 1 }}>Show</SectionLabel>
          <FilterRow
            icon={<ViewKanbanOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="All tasks"
            count={counts.all}
            selected={scope === 'all'}
            onClick={() => onScopeChange('all')}
          />
          <FilterRow
            icon={<PersonOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="Assigned to me"
            count={counts.me}
            selected={scope === 'me'}
            onClick={() => onScopeChange('me')}
          />
          {counts.teams.length > 0 && (
            <SectionLabel sx={{ pt: 1.5, pb: 0.75 }}>My teams</SectionLabel>
          )}
          {counts.teams.map((team) => (
            <FilterRow
              key={team.name}
              icon={
                <Dot
                  size={10}
                  color={resolveColor(theme, getTeamColorKey(team.name))}
                  sx={{ mx: 0.5 }}
                />
              }
              label={team.name}
              count={team.count}
              selected={scope === `team:${team.name}`}
              onClick={() => onScopeChange(`team:${team.name}`)}
            />
          ))}
          <Divider sx={{ my: 1 }} />
          <FormControlLabel
            sx={{ mx: 0.5, mb: 0.5 }}
            control={
              <Switch
                size="small"
                checked={includeCompleted}
                onChange={(e) => onIncludeCompletedChange(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Show completed (last 7 days)
              </Typography>
            }
          />
        </Stack>
      </Paper>
    </Stack>
  )
}

export default TaskFocusRail
